import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Backdrop,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add,
  Cancel,
  Delete,
  Edit,
  Save,
  Menu as MenuIcon,
  FilterList,
  VisibilityOutlined as VisibilityOff,
  Visibility as VisibilityIcon,
  ContentCopy,
  VpnKeyOutlined,
  LockOutlined,
  Refresh,
} from '@mui/icons-material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import FolderOutlined from '@mui/icons-material/FolderOutlined';
import { Logger } from 'utils/Logger';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { apiClient } from 'utils/ApiClient';
import { useModulePermissions } from 'hooks/useModulePermissions';
import { DataTableWithStaticSchema } from 'components/DataTable';
import { User } from 'modules/Interfaces/Company';
import { getAnamnesisTileCenterColors } from './anamnesisTileTheme';

export interface AnamnesisRepo {
  _id: string;
  companyId?: string;
  projectName: string;
  ideUpload: boolean;
  gitRepoUrl?: string;
  gitUsername?: string;
  gitPersonalAccessToken?: string;
  gitSyncFrequencySeconds?: number;
  status: 'active' | 'inactive';
  visibility?: 'private' | 'company';
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AnamnesisSecretKey {
  _id: string;
  name: string;
  secret?: string;
  createdAt?: string;
}

const VIEWMODE_STORAGE_KEY = 'anamnesis-settings-view-mode';
const HTTPS_URL_REGEX = /^https:\/\/.+/i;

export const Settings: React.FC = () => {
  const theme = useTheme();
  const tileCenterColors = getAnamnesisTileCenterColors(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const user = useSelector((state: RootState) => state.authentication.user);
  const { hasAnamnesisCloudAccess, isUserSuperAdmin } = useModulePermissions();

  const [repos, setRepos] = useState<AnamnesisRepo[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [currentRepo, setCurrentRepo] = useState<Partial<AnamnesisRepo> | null>(null);
  const [currentStatusRepo, setCurrentStatusRepo] = useState<AnamnesisRepo | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [toolbarMenuOpen, setToolbarMenuOpen] = useState(false);
  const [showPat, setShowPat] = useState<boolean>(false);

  const [viewMode, setViewMode] = useState<'tile' | 'table'>(() => {
    const stored = localStorage.getItem(VIEWMODE_STORAGE_KEY);
    return stored === 'table' ? 'table' : 'tile';
  });
  const [tableRefreshTrigger, setTableRefreshTrigger] = useState<number>(0);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortField, setSortField] = useState<string>('updatedAt');
  const [sortOrder, setSortOrder] = useState<string>('desc');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Credentials dialog state
  const [credentialsOpen, setCredentialsOpen] = useState<boolean>(false);
  const [secretKeys, setSecretKeys] = useState<AnamnesisSecretKey[]>([]);
  const [newKeyName, setNewKeyName] = useState<string>('');
  const [newlyCreatedSecret, setNewlyCreatedSecret] = useState<{ _id: string; name: string; secret: string } | null>(null);
  const [secretsLoading, setSecretsLoading] = useState<boolean>(false);
  const [creatorNameById, setCreatorNameById] = useState<Record<string, string>>({});

  const company = useSelector((state: RootState) => state.authentication.company);

  const showMessage = useCallback((message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const fetchRepos = async () => {
    setLoading(true);
    try {
      let endpoint = '/anamnesis/repos';
      if (statusFilter !== 'active') endpoint += `?status=${statusFilter}`;
      const data = await apiClient.get(endpoint);
      setRepos(Array.isArray(data.data) ? (data.data as AnamnesisRepo[]) : []);
    } catch (error) {
      Logger.error('Anamnesis Settings: failed to fetch repos:', error);
      showMessage(`Failed to fetch repositories: ${error instanceof Error ? error.message : String(error)}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRepos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    const loadCompanyUsers = async () => {
      const companyId = user?.companyId;
      if (!companyId) return;
      try {
        const data = await apiClient.get(`/user/all/${companyId}`);
        const users = Array.isArray(data.data) ? (data.data as User[]) : [];
        const map: Record<string, string> = {};
        users.forEach((companyUser) => {
          const name = [companyUser.profile?.firstName, companyUser.profile?.lastName].filter(Boolean).join(' ').trim();
          map[String(companyUser._id)] = name || companyUser.email || String(companyUser._id);
        });
        setCreatorNameById(map);
      } catch (error) {
        Logger.error('Anamnesis Settings: failed to fetch company users for Created By labels:', error);
      }
    };
    loadCompanyUsers();
  }, [user?.companyId]);

  const handleRefresh = async () => {
    await fetchRepos();
    setTableRefreshTrigger((prev) => prev + 1);
  };

  const getCreatedByLabel = useCallback((createdBy?: string) => {
    if (!createdBy) return '—';
    if (String(createdBy) === String(user?._id)) {
      const selfName = [user?.profile?.firstName, user?.profile?.lastName].filter(Boolean).join(' ').trim();
      return selfName || user?.email || 'You';
    }
    return creatorNameById[String(createdBy)] || createdBy;
  }, [creatorNameById, user]);

  const isRepoOwner = useCallback((repo: Pick<AnamnesisRepo, 'createdBy'>) => {
    return String(repo.createdBy) === String(user?._id);
  }, [user?._id]);

  const enrichReposWithCreator = useCallback((data: any[]) => data.map((row) => ({
    ...row,
    createdByName: getCreatedByLabel(row.createdBy),
  })), [getCreatedByLabel]);

  const validateForm = (repo: Partial<AnamnesisRepo> | null): boolean => {
    const errors: Record<string, string> = {};
    if (!repo?.projectName || !repo.projectName.trim()) errors.projectName = 'Project Name is required';
    if (!repo?.ideUpload) {
      if (!repo?.gitRepoUrl) errors.gitRepoUrl = 'Git Repo URL is required';
      else if (!HTTPS_URL_REGEX.test(repo.gitRepoUrl)) errors.gitRepoUrl = 'HTTPS url only is allowed';
      if (!repo?.gitUsername) errors.gitUsername = 'Git Username is required';
      if (!isEditing && !repo?.gitPersonalAccessToken) errors.gitPersonalAccessToken = 'Git Personal Access Token is required';
      const freqRaw = repo?.gitSyncFrequencySeconds;
      if (freqRaw !== undefined && freqRaw !== 0 && (!Number.isInteger(freqRaw) || freqRaw <= 0)) {
        errors.gitSyncFrequencySeconds = 'Must be a positive integer (seconds)';
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveRepo = async () => {
    if (!currentRepo) return;
    if (!validateForm(currentRepo)) {
      showMessage('Please fix the form errors', 'error');
      return;
    }
    setLoading(true);
    try {
      const payload: any = {
        projectName: currentRepo.projectName?.trim(),
        ideUpload: !!currentRepo.ideUpload,
        gitRepoUrl: currentRepo.ideUpload ? '' : currentRepo.gitRepoUrl?.trim() || '',
        gitUsername: currentRepo.ideUpload ? '' : currentRepo.gitUsername?.trim() || '',
        gitSyncFrequencySeconds: currentRepo.ideUpload ? 0 : Number(currentRepo.gitSyncFrequencySeconds ?? 0),
        status: currentRepo.status || 'active',
        visibility: currentRepo.visibility === 'company' ? 'company' : 'private',
      };
      if (!currentRepo.ideUpload && currentRepo.gitPersonalAccessToken) {
        payload.gitPersonalAccessToken = currentRepo.gitPersonalAccessToken;
      }
      if (isEditing) {
        await apiClient.patch(`/anamnesis/repos/${currentRepo._id}`, payload);
      } else {
        await apiClient.post('/anamnesis/repos', payload);
      }
      await fetchRepos();
      setTableRefreshTrigger(prev => prev + 1);
      showMessage('Repository saved successfully!', 'success');
      closeDialog();
    } catch (error) {
      Logger.error('Anamnesis Settings: failed to save repo:', error);
      showMessage(`Failed to save repository: ${error instanceof Error ? error.message : String(error)}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStatus = async () => {
    if (!currentStatusRepo?._id) return;
    setLoading(true);
    try {
      await apiClient.patch(`/anamnesis/repos/${currentStatusRepo._id}/status`, {
        status: currentStatusRepo.status,
        visibility: currentStatusRepo.visibility === 'company' ? 'company' : 'private',
      });
      await fetchRepos();
      setTableRefreshTrigger(prev => prev + 1);
      showMessage('Repository updated successfully!', 'success');
      closeStatusDialog();
    } catch (error) {
      Logger.error('Anamnesis Settings: failed to update status:', error);
      showMessage(`Failed to update repository: ${error instanceof Error ? error.message : String(error)}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRepo = async (repoId: string) => {
    setLoading(true);
    try {
      await apiClient.delete(`/anamnesis/repos/${repoId}`);
      await fetchRepos();
      setTableRefreshTrigger(prev => prev + 1);
      showMessage('Repository deleted successfully!', 'success');
    } catch (error) {
      Logger.error('Anamnesis Settings: failed to delete repo:', error);
      showMessage(`Failed to delete repository: ${error instanceof Error ? error.message : String(error)}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // ===== Credentials / Secret keys =====
  const handleCopy = (value: string, label: string) => {
    navigator.clipboard.writeText(value);
    showMessage(`${label} copied to clipboard!`, 'success');
  };

  const handleCopyExistingSecret = async (keyId: string) => {
    setSecretsLoading(true);
    try {
      const response = await apiClient.get(`/anamnesis/secrets/${keyId}`);
      const secret = response.data?.secret;
      if (secret) {
        handleCopy(secret, 'Secret key');
      } else {
        showMessage('Secret key not found', 'error');
      }
    } catch (error) {
      Logger.error('Anamnesis Settings: failed to reveal secret:', error);
      showMessage(`Failed to reveal secret key: ${error instanceof Error ? error.message : String(error)}`, 'error');
    } finally {
      setSecretsLoading(false);
    }
  };

  const fetchSecrets = async () => {
    setSecretsLoading(true);
    try {
      const data = await apiClient.get('/anamnesis/secrets');
      setSecretKeys(Array.isArray(data.data?.keys) ? data.data.keys : []);
    } catch (error) {
      Logger.error('Anamnesis Settings: failed to fetch secrets:', error);
      showMessage(`Failed to fetch secret keys: ${error instanceof Error ? error.message : String(error)}`, 'error');
    } finally {
      setSecretsLoading(false);
    }
  };

  const openCredentialsDialog = () => {
    setNewKeyName('');
    setNewlyCreatedSecret(null);
    setCredentialsOpen(true);
    fetchSecrets();
  };

  const handleAddSecret = async () => {
    const name = newKeyName.trim();
    if (!name) {
      showMessage('Please enter a name for the secret key', 'error');
      return;
    }
    setSecretsLoading(true);
    try {
      const data = await apiClient.post('/anamnesis/secrets', { name });
      if (data.data?.secret) {
        setNewlyCreatedSecret({ _id: data.data._id, name: data.data.name, secret: data.data.secret });
        setNewKeyName('');
        await fetchSecrets();
        showMessage('Secret key created. Copy it now — it will not be shown again.', 'success');
      } else {
        showMessage(data?.message || 'Failed to create secret key', 'error');
      }
    } catch (error) {
      Logger.error('Anamnesis Settings: failed to create secret:', error);
      showMessage(`Failed to create secret key: ${error instanceof Error ? error.message : String(error)}`, 'error');
    } finally {
      setSecretsLoading(false);
    }
  };

  const handleDeleteSecret = async (keyId: string) => {
    setSecretsLoading(true);
    try {
      await apiClient.delete(`/anamnesis/secrets/${keyId}`);
      if (newlyCreatedSecret?._id === keyId) setNewlyCreatedSecret(null);
      await fetchSecrets();
      showMessage('Secret key deleted', 'success');
    } catch (error) {
      Logger.error('Anamnesis Settings: failed to delete secret:', error);
      showMessage(`Failed to delete secret key: ${error instanceof Error ? error.message : String(error)}`, 'error');
    } finally {
      setSecretsLoading(false);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setCurrentRepo(null);
    setFormErrors({});
    setShowPat(false);
  };

  const closeStatusDialog = () => {
    setStatusDialogOpen(false);
    setCurrentStatusRepo(null);
  };

  const openNewRepoDialog = (repo?: AnamnesisRepo) => {
    setCurrentRepo(
      repo
        ? { ...repo }
        : {
            projectName: '',
            ideUpload: false,
            gitRepoUrl: '',
            gitUsername: '',
            gitPersonalAccessToken: '',
            gitSyncFrequencySeconds: 0,
            status: 'active',
            visibility: 'private',
          },
    );
    setFormErrors({});
    setShowPat(false);
    setIsEditing(!!repo);
    setDialogOpen(true);
  };

  const openStatusDialog = (repo: AnamnesisRepo) => {
    setCurrentStatusRepo({ ...repo });
    setStatusDialogOpen(true);
  };

  const visibleRepos = useMemo(() => {
    if (isUserSuperAdmin) return repos;
    const currentUserId = user?._id?.toString();
    return repos.filter((repo) => repo.visibility === 'company' || String(repo.createdBy) === currentUserId);
  }, [repos, isUserSuperAdmin, user]);

  const filteredAndSortedRepos = useMemo(() => {
    let filtered = visibleRepos.filter((repo) => {
      const name = repo.projectName?.toLowerCase() || '';
      const matchesSearch = name.includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || repo.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
    filtered.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'projectName') {
        comparison = (a.projectName || '').localeCompare(b.projectName || '');
      } else if (sortField === 'updatedAt') {
        const dA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const dB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        comparison = dA - dB;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return filtered;
  }, [visibleRepos, searchTerm, sortField, sortOrder, statusFilter]);

  // Table view schema
  const reposSchema = useMemo(() => ({
    paths: {
      _id: { instance: 'String', isRequired: false },
      projectName: { instance: 'String', isRequired: false },
      ideUpload: { instance: 'Boolean', isRequired: false },
      gitRepoUrl: { instance: 'String', isRequired: false },
      gitUsername: { instance: 'String', isRequired: false },
      gitSyncFrequencySeconds: { instance: 'Number', isRequired: false },
      status: { instance: 'String', isRequired: false },
      visibility: { instance: 'String', isRequired: false },
      createdByName: { instance: 'String', isRequired: false },
      createdAt: { instance: 'Date', isRequired: false },
      updatedAt: { instance: 'Date', isRequired: false },
    },
    obj: {},
  }), []);

  const REPOS_MANDATORY_COLUMNS = useMemo(() => ['projectName', 'visibility', 'createdByName'], []);
  const REPOS_OPTIONAL_COLUMNS = useMemo(() => ['status', 'ideUpload', 'gitRepoUrl', 'gitUsername', 'gitSyncFrequencySeconds', 'createdAt', 'updatedAt'], []);
  const REPOS_DISABLED_FILTER_COLUMNS = useMemo(() => ['gitSyncFrequencySeconds', 'createdAt', 'updatedAt'], []);

  const tableDataUrl = useMemo(() => {
    let endpoint = '/anamnesis/repos';
    if (statusFilter !== 'active') endpoint += `?status=${statusFilter}`;
    return endpoint;
  }, [statusFilter]);

  const canManage = hasAnamnesisCloudAccess('admin') || isUserSuperAdmin;

  const renderVisibilityChip = (visibility?: 'private' | 'company', tile = false) => {
    const isShared = visibility === 'company';
    return (
      <Chip
        label={isShared ? 'Shared' : 'Private'}
        color={isShared ? 'error' : 'success'}
        variant="outlined"
        size="small"
        sx={{
          height: 22,
          fontSize: '0.68rem',
          fontWeight: 700,
          ...(tile ? { boxShadow: '0 1px 4px rgba(0,0,0,0.3)', backgroundColor: 'rgba(255,255,255,0.92)' } : {}),
        }}
      />
    );
  };

  const renderToolbar = (variant: 'desktop' | 'mobile-dialog' = 'desktop') => (
    <>
      <TextField
        label="Search Repositories"
        variant="outlined"
        size="small"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        fullWidth={variant === 'mobile-dialog'}
        sx={variant === 'desktop' ? { flexGrow: 1, minWidth: '200px' } : undefined}
      />
      <FormControl fullWidth={variant === 'mobile-dialog'} sx={variant === 'desktop' ? { minWidth: 170 } : undefined} size="small">
        <InputLabel>Sort By</InputLabel>
        <Select
          value={sortField}
          label="Sort By"
          onChange={(e) => setSortField(e.target.value as string)}
        >
          <MenuItem value="updatedAt">Last Updated On</MenuItem>
          <MenuItem value="projectName">Project Name</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth={variant === 'mobile-dialog'} sx={variant === 'desktop' ? { minWidth: 110 } : undefined} size="small">
        <InputLabel>Order</InputLabel>
        <Select
          value={sortOrder}
          label="Order"
          onChange={(e) => setSortOrder(e.target.value as string)}
        >
          <MenuItem value="asc">ASC</MenuItem>
          <MenuItem value="desc">DSC</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth={variant === 'mobile-dialog'} sx={variant === 'desktop' ? { minWidth: 130 } : undefined} size="small">
        <InputLabel>Status</InputLabel>
        <Select
          value={statusFilter}
          label="Status"
          onChange={(e) => setStatusFilter(e.target.value as string)}
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
          <MenuItem value="all">All Status</MenuItem>
        </Select>
      </FormControl>
      <Box display="flex" alignItems="center" gap={1}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_e, newMode) => {
            if (newMode !== null) {
              setViewMode(newMode);
              localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
            }
          }}
          size="small"
          sx={{ '& .MuiToggleButton-root': { border: '1px solid', borderColor: 'divider' } }}
        >
          <ToggleButton value="tile" aria-label="Tile view">
            <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
          </ToggleButton>
          <ToggleButton value="table" aria-label="Table view">
            <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </>
  );

  return (
    <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3}>
      {loading && (
        <Backdrop sx={{ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <DynamicBreadcrumbs items={[
        { itemPath: '/dashboard', itemLabel: 'Home' },
        { itemPath: '/anamnesis/settings', itemLabel: 'Anamnesis Settings' },
      ]} />

      {canManage && (
        <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 2, px: { xs: 0.5, sm: 1 } }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<Add />}
            onClick={() => openNewRepoDialog()}
            fullWidth
            sx={{ maxWidth: 480, py: 1.25, fontWeight: 700, letterSpacing: '0.04em', borderWidth: 2, borderColor: 'secondary.main', color: 'secondary.main', textTransform: 'uppercase', '&:hover': { borderWidth: 2, borderColor: 'secondary.dark' } }}
          >
            New Project
          </Button>
        </Box>
      )}

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: { xs: 1, md: 2 },
          alignItems: 'center',
          mb: 3,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
        }}
      >
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
          <IconButton color="secondary" onClick={() => setMobileMenuOpen(true)} size="large" sx={{ border: '1px solid', borderColor: 'secondary.main', '&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' } }}>
            <FilterList />
          </IconButton>
        </Box>
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', ml: 'auto' }}>
          <IconButton color="secondary" onClick={() => setToolbarMenuOpen(true)} size="large" sx={{ border: '1px solid', borderColor: 'secondary.main', '&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' } }}>
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Desktop: search on the left, controls + New Repo on the right — single row */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexGrow: 1,
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {/* Left: search */}
          <TextField
            label="Search Repositories"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1, minWidth: '220px', flexShrink: 1 }}
          />

          {/* Right: controls + New Repo button */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'nowrap',
              flexShrink: 0,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortField}
                label="Sort By"
                onChange={(e) => setSortField(e.target.value as string)}
              >
                <MenuItem value="updatedAt">Last Updated On</MenuItem>
                <MenuItem value="projectName">Project Name</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Order</InputLabel>
              <Select
                value={sortOrder}
                label="Order"
                onChange={(e) => setSortOrder(e.target.value as string)}
              >
                <MenuItem value="asc">ASC</MenuItem>
                <MenuItem value="desc">DSC</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value as string)}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="all">All Status</MenuItem>
              </Select>
            </FormControl>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_e, newMode) => {
                if (newMode !== null) {
                  setViewMode(newMode);
                  localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
                }
              }}
              size="small"
              sx={{ '& .MuiToggleButton-root': { border: '1px solid', borderColor: 'divider' } }}
            >
              <ToggleButton value="tile" aria-label="Tile view">
                <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
              </ToggleButton>
              <ToggleButton value="table" aria-label="Table view">
                <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>

            {canManage && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Add />}
                onClick={() => openNewRepoDialog()}
                size="small"
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                New Project
              </Button>
            )}
            {canManage && (
              <Tooltip title="View Credentials">
                <Button
                  variant="contained"
                  color="warning"
                  onClick={openCredentialsDialog}
                  size="small"
                  sx={{ minWidth: 36, px: 1, flexShrink: 0 }}
                  aria-label="View Credentials"
                >
                  <LockOutlined fontSize="small" />
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Refresh">
              <Button
                variant="contained"
                color="secondary"
                onClick={handleRefresh}
                size="small"
                sx={{ minWidth: 36, px: 1, flexShrink: 0 }}
                aria-label="Refresh"
              >
                <Refresh fontSize="small" />
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Mobile filter dialog */}
      <Dialog open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Search &amp; Filters</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {renderToolbar('mobile-dialog')}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMobileMenuOpen(false)} variant="outlined" color="secondary">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Mobile toolbar actions dialog */}
      <Dialog open={toolbarMenuOpen} onClose={() => setToolbarMenuOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Actions</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_e, newMode) => { if (newMode !== null) { setViewMode(newMode); localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode); } }}
                size="small"
                sx={{ '& .MuiToggleButton-root': { border: '1px solid', borderColor: 'divider' } }}
              >
                <ToggleButton value="tile" aria-label="Tile view"><Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip></ToggleButton>
                <ToggleButton value="table" aria-label="Table view"><Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip></ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Tooltip title="Refresh">
              <Button
                variant="contained"
                color="secondary"
                onClick={() => { handleRefresh(); setToolbarMenuOpen(false); }}
                size="small"
                sx={{ minWidth: 36, px: 1, alignSelf: 'flex-start' }}
                aria-label="Refresh"
              >
                <Refresh fontSize="small" />
              </Button>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToolbarMenuOpen(false)} variant="outlined" color="secondary">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Tile View (default) */}
      {viewMode === 'tile' && (
        <Box display="flex" flexWrap="wrap" gap={2.5} sx={{ justifyContent: { xs: 'center', lg: 'flex-start' } }}>
          {filteredAndSortedRepos.map((repo) => {
            const isInactive = repo.status === 'inactive';
            return (
              <Box
                key={repo._id}
                sx={{
                  width: 260,
                  height: 200,
                  borderRadius: 2.5,
                  overflow: 'hidden',
                  position: 'relative',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  cursor: 'pointer',
                  flexShrink: 0,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover .tile-overlay': { height: 120 },
                  '&:hover .tile-details': { opacity: 1 },
                  '&:hover .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                  '@media (hover: none)': {
                    '& .tile-overlay': { height: 120 },
                    '& .tile-details': { opacity: 1 },
                    '& .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                  },
                }}
              >
                <Box sx={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  background: theme.palette.dashboard?.counterHeroGradient ?? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                }}>
                  <FolderOutlined sx={{ fontSize: 40, color: tileCenterColors.foreground, opacity: 0.85 }} />
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: tileCenterColors.foreground, lineHeight: 1, textShadow: tileCenterColors.textShadow, textAlign: 'center', px: 1, wordBreak: 'break-word' }}>
                    {repo.ideUpload ? 'IDE Upload' : 'Server Pull'}
                  </Typography>
                  {typeof repo.gitSyncFrequencySeconds === 'number' && repo.gitSyncFrequencySeconds > 0 && (
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: tileCenterColors.muted, textTransform: 'uppercase', letterSpacing: 1 }}>
                      Sync {repo.gitSyncFrequencySeconds}s
                    </Typography>
                  )}
                </Box>

                <Box sx={{ position: 'absolute', top: 8, left: 8, right: 8, zIndex: 2, display: 'flex', gap: 0.5 }}>
                  <Chip
                    label={repo.ideUpload ? 'IDE' : 'GIT'}
                    color={repo.ideUpload ? 'info' : 'warning'}
                    size="small"
                    sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                  />
                  <Chip
                    label={repo.status}
                    color={repo.status === 'active' ? 'success' : 'warning'}
                    size="small"
                    sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.3)', textTransform: 'capitalize' }}
                  />
                  {renderVisibilityChip(repo.visibility, true)}
                </Box>

                <Box
                  className="tile-overlay"
                  sx={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    overflow: 'hidden',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.72) 50%, rgba(0,0,0,0.25) 100%)',
                    transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                    height: 40, px: 1.5,
                    display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
                  }}
                >
                  <Typography variant="subtitle2" noWrap className="tile-name-short" sx={{
                    color: '#fff', fontWeight: 700, fontSize: '0.82rem',
                    lineHeight: '40px', textShadow: '0 1px 3px rgba(0,0,0,0.4)', flexShrink: 0,
                    transition: 'opacity 0.2s ease, height 0.2s ease, line-height 0.2s ease',
                  }}>
                    {repo.projectName}
                  </Typography>
                  <Box className="tile-details" sx={{
                    opacity: 0, transition: 'opacity 0.3s ease 0.08s',
                    display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.25, pb: 1, flex: 1,
                  }}>
                    <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.78rem', lineHeight: 1.3, textShadow: '0 1px 3px rgba(0,0,0,0.4)', wordBreak: 'break-word' }}>
                      {repo.projectName}
                    </Typography>
                    {!repo.ideUpload && repo.gitRepoUrl && (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2, wordBreak: 'break-all' }}>
                        {repo.gitRepoUrl}
                      </Typography>
                    )}
                    {repo.updatedAt && (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}>
                        Updated: {new Date(repo.updatedAt).toLocaleDateString()}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}>
                      Created by: {getCreatedByLabel(repo.createdBy)}
                    </Typography>
                    {canManage && isRepoOwner(repo) && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                        <Box display="flex" gap={0.5}>
                          <Tooltip title="Edit">
                            <Button color="secondary" variant="contained" size="small"
                              onClick={(e) => { e.stopPropagation(); openStatusDialog(repo); }}
                              sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                              <Edit fontSize="small" />
                            </Button>
                          </Tooltip>
                        </Box>
                        <Box display="flex" gap={0.5}>
                          <Tooltip title="Delete">
                            <Button color="error" variant="contained" size="small"
                              onClick={(e) => { e.stopPropagation(); handleDeleteRepo(repo._id); }}
                              sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                              <Delete fontSize="small" />
                            </Button>
                          </Tooltip>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
          {filteredAndSortedRepos.length === 0 && (
            <Box width="100%" display="flex" justifyContent="center" alignItems="center" py={6}>
              <Typography variant="body2" color="text.secondary">No repositories found. Click &quot;New Repo&quot; to add one.</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: 'background.paper', p: 2 }}>
          <DataTableWithStaticSchema
            dataUrl={tableDataUrl}
            schemaObject={reposSchema}
            columnsToExclude={['_id', 'companyId', 'createdBy', 'updatedBy', 'gitPersonalAccessToken']}
            refreshTrigger={tableRefreshTrigger}
            customDataProcessor={enrichReposWithCreator}
            initialSortColumn="updatedAt"
            initialSortOrder="desc"
            dataSetName="anamnesis-repos-list"
            enableColumnPreferences={true}
            mandatoryColumns={REPOS_MANDATORY_COLUMNS}
            allowedOptionalColumns={REPOS_OPTIONAL_COLUMNS}
            columnsWithDisabledFilter={REPOS_DISABLED_FILTER_COLUMNS}
            columnRenderers={{
              status: (row: any) => (
                <Chip label={row.status} color={row.status === 'active' ? 'success' : 'warning'} variant="outlined" size="small" sx={{ textTransform: 'capitalize' }} />
              ),
              ideUpload: (row: any) => (
                <Chip label={row.ideUpload ? 'IDE' : 'GIT'} color={row.ideUpload ? 'info' : 'warning'} variant="outlined" size="small" />
              ),
              visibility: (row: any) => renderVisibilityChip(row.visibility),
              gitSyncFrequencySeconds: (row: any) => (typeof row.gitSyncFrequencySeconds === 'number' && row.gitSyncFrequencySeconds > 0 ? `${row.gitSyncFrequencySeconds}s` : '—'),
            }}
            customActions={(row: any) => {
              const actions: { label: string; onClick: (r: any) => void; color: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'; icon: React.ReactNode }[] = [];
              if (canManage && isRepoOwner(row)) {
                actions.push({ label: 'Edit', onClick: (r) => openStatusDialog(r), color: 'secondary', icon: <Edit /> });
                actions.push({ label: 'Delete', onClick: (r) => handleDeleteRepo(r._id), color: 'error', icon: <Delete /> });
              }
              return actions;
            }}
          />
        </Box>
      )}

      <Box>
        {/* New Repo / Edit Dialog */}
        <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
          <DialogTitle>{isEditing ? 'Edit Repository' : 'New Project'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Project Name"
                size="small"
                value={currentRepo?.projectName || ''}
                onChange={(e) => setCurrentRepo((prev) => ({ ...prev!, projectName: e.target.value }))}
                fullWidth
                required
                error={!!formErrors.projectName}
                helperText={formErrors.projectName}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!currentRepo?.ideUpload}
                    onChange={(e) => setCurrentRepo((prev) => ({ ...prev!, ideUpload: e.target.checked }))}
                    color="secondary"
                  />
                }
                label="IDE Upload"
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                Knowledge Graph will be uploaded from IDE?
              </Typography>

              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={currentRepo?.visibility === 'company' ? 'company' : 'private'}
                  label="Visibility"
                  onChange={(e) => setCurrentRepo((prev) => ({ ...prev!, visibility: e.target.value as 'private' | 'company' }))}
                >
                  <MenuItem value="private">Private (only me)</MenuItem>
                  <MenuItem value="company">Share with all users of company</MenuItem>
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {currentRepo?.visibility === 'company'
                    ? 'All users in your company with Anamnesis access will see this project.'
                    : 'Only you will see this project.'}
                </Typography>
              </FormControl>

              {!currentRepo?.ideUpload && (
                <>
                  <TextField
                    label="Git Repo URL"
                    size="small"
                    value={currentRepo?.gitRepoUrl || ''}
                    onChange={(e) => setCurrentRepo((prev) => ({ ...prev!, gitRepoUrl: e.target.value }))}
                    fullWidth
                    error={!!formErrors.gitRepoUrl}
                    helperText={formErrors.gitRepoUrl || 'HTTPS url only is allowed.'}
                  />
                  <TextField
                    label="Git Username"
                    size="small"
                    value={currentRepo?.gitUsername || ''}
                    onChange={(e) => setCurrentRepo((prev) => ({ ...prev!, gitUsername: e.target.value }))}
                    fullWidth
                    error={!!formErrors.gitUsername}
                    helperText={formErrors.gitUsername}
                  />
                  <TextField
                    label="Git Personal Access Token"
                    size="small"
                    type={showPat ? 'text' : 'password'}
                    value={currentRepo?.gitPersonalAccessToken || ''}
                    onChange={(e) => setCurrentRepo((prev) => ({ ...prev!, gitPersonalAccessToken: e.target.value }))}
                    fullWidth
                    error={!!formErrors.gitPersonalAccessToken}
                    helperText={formErrors.gitPersonalAccessToken || (isEditing ? 'Leave blank to keep existing token.' : undefined)}
                    InputProps={{
                      endAdornment: (
                        <IconButton onClick={() => setShowPat((s) => !s)} edge="end" size="small">
                          {showPat ? <VisibilityOff fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      ),
                    }}
                  />
                  <TextField
                    label="Frequency of update from GIT (seconds)"
                    size="small"
                    type="number"
                    value={currentRepo?.gitSyncFrequencySeconds ?? ''}
                    onChange={(e) => setCurrentRepo((prev) => ({ ...prev!, gitSyncFrequencySeconds: e.target.value === '' ? 0 : parseInt(e.target.value, 10) }))}
                    fullWidth
                    error={!!formErrors.gitSyncFrequencySeconds}
                    helperText={formErrors.gitSyncFrequencySeconds || 'Accepts time in seconds only as number.'}
                  />
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" color="secondary" onClick={closeDialog} startIcon={<Cancel />}>Cancel</Button>
            <Button onClick={saveRepo} startIcon={<Save />} variant="contained" color="success">Save</Button>
          </DialogActions>
        </Dialog>

        {/* Status Dialog */}
        <Dialog open={statusDialogOpen} onClose={closeStatusDialog} fullWidth maxWidth="sm">
          <DialogTitle>Edit Repository Settings</DialogTitle>
          <DialogContent>
            <Typography variant="h6" sx={{ mb: 2 }}>{currentStatusRepo?.projectName}</Typography>
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                size="small"
                value={currentStatusRepo?.status || 'active'}
                label="Status"
                onChange={(e) => setCurrentStatusRepo((prev) => prev ? { ...prev, status: e.target.value as 'active' | 'inactive' } : null)}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Visibility</InputLabel>
              <Select
                size="small"
                value={currentStatusRepo?.visibility === 'company' ? 'company' : 'private'}
                label="Visibility"
                onChange={(e) => setCurrentStatusRepo((prev) => prev ? { ...prev, visibility: e.target.value as 'private' | 'company' } : null)}
              >
                <MenuItem value="private">Private (only me)</MenuItem>
                <MenuItem value="company">Share with all users of company</MenuItem>
              </Select>
            </FormControl>
            <Button sx={{ mt: 2 }} variant="outlined" color="secondary" startIcon={<Edit />} onClick={() => { const r = currentStatusRepo; if (r) { closeStatusDialog(); openNewRepoDialog(r); } }}>
              Edit Full Details
            </Button>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" color="secondary" onClick={closeStatusDialog} startIcon={<Cancel />}>Cancel</Button>
            <Button onClick={handleSaveStatus} startIcon={<Save />} variant="contained" color="success">Save</Button>
          </DialogActions>
        </Dialog>

        {/* Credentials Dialog */}
        <Dialog open={credentialsOpen} onClose={() => setCredentialsOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VpnKeyOutlined fontSize="small" /> View Credentials
          </DialogTitle>
          <DialogContent>
            {secretsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {/* Client ID = companyId */}
              <Box>
                <Typography variant="caption" color="text.secondary">Client Id</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <TextField
                    size="small"
                    value={company?._id || ''}
                    InputProps={{ readOnly: true }}
                    fullWidth
                    sx={{ '& .MuiInputBase-input': { cursor: 'default' } }}
                  />
                  <Tooltip title="Copy Client Id">
                    <IconButton onClick={() => handleCopy(company?._id || '', 'Client Id')} edge="end" color="secondary" size="small" sx={{ border: '1px solid', borderColor: 'secondary.main' }}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box mt={2}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Secret Keys</Typography>

                {/* Add new secret */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    size="small"
                    label="Key Name"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    fullWidth
                  />
                  <Button variant="contained" color="secondary" startIcon={<Add />} onClick={handleAddSecret} sx={{ whiteSpace: 'nowrap' }}>
                    Add Key
                  </Button>
                </Box>

                {/* Newly created secret one-time reveal */}
                {newlyCreatedSecret && (
                  <Box sx={{
                    p: 1.5,
                    mb: 2,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'warning.main',
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,152,0,0.08)' : 'rgba(255,152,0,0.05)',
                  }}>
                    <Typography variant="caption" color="warning.main" sx={{ fontWeight: 700 }}>
                      Copy this secret now — it will not be shown again.
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                        {newlyCreatedSecret.name}: <strong>{newlyCreatedSecret.secret}</strong>
                      </Typography>
                      <Tooltip title="Copy Secret">
                        <IconButton onClick={() => handleCopy(newlyCreatedSecret.secret, 'Secret')} edge="end" color="secondary" size="small" sx={{ border: '1px solid', borderColor: 'secondary.main' }}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                )}

                {/* List existing keys */}
                {secretKeys.length === 0 && !newlyCreatedSecret ? (
                  <Typography variant="body2" color="text.secondary">No secret keys yet. Add one above to get started.</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {secretKeys.map((key) => (
                      <Box key={key._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <VpnKeyOutlined fontSize="small" color="action" />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap>{key.name}</Typography>
                          {key.createdAt && (
                            <Typography variant="caption" color="text.secondary">
                              Created: {new Date(key.createdAt).toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                        <Tooltip title="Copy secret key">
                          <IconButton onClick={() => handleCopyExistingSecret(key._id)} edge="end" color="secondary" size="small" sx={{ border: '1px solid', borderColor: 'secondary.main' }}>
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete key">
                          <IconButton onClick={() => handleDeleteSecret(key._id)} edge="end" color="error" size="small">
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" color="secondary" onClick={() => setCredentialsOpen(false)} startIcon={<Cancel />}>Close</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default Settings;
